import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { Users } from 'lucide-react'

export interface TopCustomerPoint {
  customer: string
  order_count: number
  revenue: number
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
]

function truncate(name: string, max = 18): string {
  return name.length > max ? `${name.slice(0, max - 1)}…` : name
}

export default function TopCustomersChart({
  data,
  loading,
}: {
  data: TopCustomerPoint[]
  loading: boolean
}) {
  const chartData = data.map(d => ({ ...d, label: truncate(d.customer) }))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="w-4 h-4" />
          Top customers
        </CardTitle>
        <CardDescription>By total revenue</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[320px] w-full" />
        ) : chartData.length === 0 ? (
          <p className="py-16 text-sm text-muted-foreground text-center">No orders yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(320, data.length * 44 + 48)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.65} stroke="hsl(var(--border))" horizontal={false} />
              <XAxis axisLine={false} tickLine={false} tickMargin={10}
                type="number"
                tickFormatter={value => currencyFormatter.format(Number(value))}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis axisLine={false} tickLine={false} tickMargin={10}
                type="category"
                dataKey="label"
                width={120}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />
              <Tooltip
                formatter={(value: unknown, _name: unknown, item: { payload?: TopCustomerPoint }): [
                  string,
                  string,
                ] => [
                  `${currencyFormatter.format(Number(value))} · ${item?.payload?.order_count ?? 0} orders`,
                  'Revenue',
                ]}
                labelFormatter={(_label: unknown, payload: readonly { payload?: TopCustomerPoint }[]) =>
                  payload?.[0]?.payload?.customer ?? ''
                }
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  color: 'hsl(var(--popover-foreground))',
                }}
              />
              <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={entry.customer} fill={COLORS[index % COLORS.length] ?? 'hsl(var(--chart-1))'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
